import { Slider, ToggleButton, ToggleButtonGroup } from '@chronosphereio/chrono-ui';
import { useOptimisticState } from '@chronosphereio/shared';
import { produce } from 'immer';
import {
  NullBehaviorOptions,
  StackOptions,
  TIME_SERIES_HIDE_AREA_OPACITY_LIMIT,
  TimeSeriesChartVisualOptions,
} from '@/core';
import {
  ColorPaletteSelector,
  OptionsEditorControl,
  OptionsEditorGroup,
  SettingsAutocomplete,
} from '@/plugin-editor-components';
import {
  DEFAULT_AREA_OPACITY,
  DEFAULT_LINE_WIDTH,
  NULL_BEHAVIOR_CONFIG,
  NULL_BEHAVIOR_OPTIONS,
  NULL_BEHAVIOR_UNDEF_LABEL_NAME,
  STACK_CONFIG,
  STACK_OPTIONS,
  VISUAL_CONFIG,
} from '../time-series-chart-model';

// Since moving slider triggers rerender of panel preview, delay slightly. This value should be adjusted as needed.
export const VISUAL_OPTIONS_SLIDER_INPUT_DELAY_MS = 200;

export interface VisualOptionsEditorProps {
  value: TimeSeriesChartVisualOptions;
  onChange: (visual: TimeSeriesChartVisualOptions) => void;
}

export function VisualOptionsEditor({ value, onChange }: VisualOptionsEditorProps) {
  // Line width slider using optimistic state to improve perceived performance
  const [lineWidth, setLineWidth] = useOptimisticState(
    value.line_width ?? DEFAULT_LINE_WIDTH,
    (newLineWidth) => {
      const nextValue = produce(value, (draft) => {
        draft.line_width = newLineWidth;
      });
      onChange(nextValue);
    },
    { delayMs: VISUAL_OPTIONS_SLIDER_INPUT_DELAY_MS }
  );
  const handleLineWidthChange = (_: Event, sliderValue: number | number[]) => {
    const newValue = Array.isArray(sliderValue) ? sliderValue[0] : sliderValue;
    if (newValue !== undefined) {
      setLineWidth(newValue);
    }
  };

  // Area opacity slider using optimistic state to improve perceived performance
  const [areaOpacity, setAreaOpacity] = useOptimisticState(
    value.area_opacity ?? DEFAULT_AREA_OPACITY,
    (newAreaOpacity) =>
      onChange(
        produce(value, (draft) => {
          // Set to undefined when 0 (the default) to remove the property and properly clear areaStyle
          draft.area_opacity = newAreaOpacity === 0 ? undefined : newAreaOpacity;
        })
      ),
    { delayMs: VISUAL_OPTIONS_SLIDER_INPUT_DELAY_MS }
  );
  const handleAreaOpacityChange = (_: Event, sliderValue: number | number[]) => {
    const newValue = Array.isArray(sliderValue) ? sliderValue[0] : sliderValue;
    if (newValue !== undefined) {
      setAreaOpacity(newValue);
    }
  };

  // Stacked area options
  const currentStack: StackOptions = value.stack ?? 'None';
  const stackConfig = STACK_CONFIG[currentStack];

  const currentNullBehavior: TimeSeriesChartVisualOptions['null_behavior'] = value.null_behavior;
  const nullBehaviorConfig = NULL_BEHAVIOR_CONFIG[currentNullBehavior ?? NULL_BEHAVIOR_UNDEF_LABEL_NAME];

  return (
    <OptionsEditorGroup title="Visual">
      <OptionsEditorControl
        label="Display"
        control={
          <ToggleButtonGroup
            value={value.display ?? 'line'}
            onChange={(__, newValue) => {
              const nextState = produce(value, (draft) => {
                draft.display = newValue;
              });
              onChange(nextState);
            }}
          >
            <ToggleButton value="line" aria-label="display line series">
              Line
            </ToggleButton>
            <ToggleButton value="bar" aria-label="display bar series">
              Bar
            </ToggleButton>
          </ToggleButtonGroup>
        }
      />
      <OptionsEditorControl
        label={VISUAL_CONFIG.line_width.label}
        control={
          <Slider
            data-testid={VISUAL_CONFIG.line_width.testId}
            value={lineWidth}
            valueLabelDisplay="auto"
            step={VISUAL_CONFIG.line_width.step}
            marks
            min={VISUAL_CONFIG.line_width.min}
            max={VISUAL_CONFIG.line_width.max}
            disabled={value.display === 'bar'}
            onChange={handleLineWidthChange}
          />
        }
      />
      <OptionsEditorControl
        label={VISUAL_CONFIG.area_opacity.label}
        description={`Area opacity will automatically turn off at ${TIME_SERIES_HIDE_AREA_OPACITY_LIMIT} series to optimize performance`}
        control={
          <Slider
            data-testid={VISUAL_CONFIG.area_opacity.testId}
            value={areaOpacity}
            valueLabelDisplay="auto"
            step={VISUAL_CONFIG.area_opacity.step}
            marks
            min={VISUAL_CONFIG.area_opacity.min}
            max={VISUAL_CONFIG.area_opacity.max}
            disabled={value.display === 'bar'}
            onChange={handleAreaOpacityChange}
          />
        }
      />
      <OptionsEditorControl
        label={VISUAL_CONFIG.stack.label}
        control={
          <SettingsAutocomplete
            value={{
              ...stackConfig,
              id: currentStack,
            }}
            options={STACK_OPTIONS}
            onChange={(__, newValue) => {
              const updatedValue: TimeSeriesChartVisualOptions = {
                ...value,
                stack: newValue.id === 'None' ? undefined : newValue.id, // stack is optional so remove property when 'None' is selected
              };
              // stacked area chart preset to automatically set area under a curve shading
              if (newValue.id === 'All' && !value.area_opacity) {
                updatedValue.area_opacity = 0.3;
              }
              onChange(updatedValue);
            }}
            disabled={value === undefined}
            disableClearable
          ></SettingsAutocomplete>
        }
      />
      <ColorPaletteSelector
        value={value.colorPalette ?? 'Categorical'}
        onChange={(newPalette) =>
          onChange({
            ...value,
            colorPalette: newPalette,
          })
        }
      />
      <OptionsEditorControl
        label={'Show points'}
        description={
          'Auto will turn off datapoint symbols when they may degrade performance. Always will force all datapoint symbols to show.'
        }
        control={
          <ToggleButtonGroup
            value={value.show_points ?? 'Auto'}
            onChange={(__, newValue: 'Auto' | 'Always' | 'Never') => {
              const nextState = produce(value, (draft) => {
                draft.show_points = newValue === 'Auto' ? undefined : newValue;
              });
              onChange(nextState);
            }}
          >
            <ToggleButton value="Auto">Auto</ToggleButton>
            <ToggleButton value="Always">Always</ToggleButton>
            <ToggleButton value="Never">Never</ToggleButton>
          </ToggleButtonGroup>
        }
      />
      <OptionsEditorControl
        label={VISUAL_CONFIG.null_behavior.label}
        control={
          <SettingsAutocomplete
            value={{
              ...nullBehaviorConfig,
              id: currentNullBehavior ?? NULL_BEHAVIOR_UNDEF_LABEL_NAME,
            }}
            options={NULL_BEHAVIOR_OPTIONS}
            onChange={(__, newValue) => {
              const nextState = produce(value, (draft) => {
                draft.null_behavior =
                  newValue.id === NULL_BEHAVIOR_UNDEF_LABEL_NAME ? undefined : (newValue.id as NullBehaviorOptions);
              });
              onChange(nextState);
            }}
            disabled={value === undefined}
            disableClearable
          ></SettingsAutocomplete>
        }
      />
    </OptionsEditorGroup>
  );
}
